import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { t } = useLang();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(identifier, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-12rem)] items-center justify-center px-4 py-12">
      <form onSubmit={submit} className="animate-fade-up w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-xl sm:p-10" data-testid="login-form">
        <h1 className="text-3xl font-extrabold tracking-tight">{t("login_title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("login_subtitle")}</p>
        <div className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label>{t("login_identifier")}</Label>
            <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder={t("login_identifier_ph")} data-testid="login-phone-input" className="h-11" required />
          </div>
          <div className="space-y-2">
            <Label>{t("password")}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-11" required />
          </div>
          {error && <p className="text-sm font-semibold text-rose-600 dark:text-rose-400" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit-button" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 font-bold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("login_submit")}
          </button>
        </div>
        <p className="mt-6 text-center text-sm text-muted-foreground">{t("no_account")} <Link to="/register" className="font-semibold text-primary" data-testid="login-register-link">{t("nav_register")}</Link></p>
      </form>
    </div>
  );
}
